import {
    CODE128B,
    PATTERNS,
    START_CODE_B,
    STOP_PATTERN,
} from "./BarcodeTypes";

export class BarcodeEncodingError extends Error {}

/**
 * Encodes the provided ascii (code 32 to 127) string to barcode patterns
 * Implementation details from:
 *  - https://en.wikipedia.org/wiki/Code_128
 *  - https://github.com/lindell/JsBarcode
 */
function encodeString(value: string): Array<Array<number>> {
    if(!CODE128B.test(value)) {
        throw new BarcodeEncodingError("Unexpected characters in barcode encoder");
    }

    const results: Array<Array<number>> = [ START_CODE_B ];

    // Start code B is 104
    let checkSum = 104;

    for(let i = 0, l = value.length; i < l; i++) {
        const index = value.charCodeAt(i) - 32;

        if(index < 0 || index > 95) {
            throw new BarcodeEncodingError("Unexpected character in barcode encoder");
        }

        checkSum += (i + 1) * index;

        results.push(PATTERNS[index]);
    }

    const checkSumIndex = checkSum % 103;

    results.push(PATTERNS[checkSumIndex], STOP_PATTERN);

    return results;
}

/**
 * Gets how many units wide the encoded barcode will be
 */
function getBarcodeWidth(encoded: Array<Array<number>>): number {
    // Quiet Zone (10) + Start Pattern (11) + Each section (n * 11) + Stop Pattern (13) + Quiet Zone (10)
    // Minus 2 because the encoded length includes the start pattern and stop pattern
    return 44 + (encoded.length - 2) * 11;
}

interface BarType {
    start: number;
    width: number;
}

/**
 * Get the solid parts of the barcode to render
 */
function getBars(encoded: Array<Array<number>>): Array<BarType> {
    const results: Array<BarType> = [];

    // Start after the quiet zone
    let currentPosition = 10;

    for(const section of encoded) {
        for(let i = 0, l = section.length; i < l; i++) {
            // Only the even indexes are solid lines
            if(i % 2 === 0) {
                results.push({
                    start: currentPosition,
                    width: section[i],
                });
            }

            currentPosition += section[i];
        }
    }

    return results;
}

const svgNameSpace = "http://www.w3.org/2000/svg";

/**
 * Renders the barcode into the provided SVG
 * Note: This is a partial implementation of Code 128 (Only Mode B)
 */
export function renderBarcodeSVG(
    svgElement: SVGSVGElement,
    height: number,
    value: string
): void {
    svgElement.replaceChildren();

    const encoded = encodeString(value);
    const width = getBarcodeWidth(encoded);
    const bars = getBars(encoded);

    // Set the attributes for the svg parent
    svgElement.setAttribute("width", `${width}px`);
    svgElement.setAttribute("height", `${height}px`);
    svgElement.setAttribute("x", "0px");
    svgElement.setAttribute("y", "0px");
    svgElement.setAttribute("viewBox", `0 0 ${width} ${height}`);

    svgElement.setAttribute("xmlns", svgNameSpace);
    svgElement.setAttribute("version", "1.1");

    // Render the bars
    for(const bar of bars) {
        const rect = document.createElementNS(svgNameSpace, "rect");

        rect.setAttribute("x", `${bar.start}px`);
        rect.setAttribute("y", "0px");
        rect.setAttribute("width", `${bar.width}px`);
        rect.setAttribute("height", `${height}px`);
        rect.setAttribute("fill", "black");

        svgElement.appendChild(rect);
    }
}
